import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import SavingOverlay from "../SavingOverlay";
import { Toast } from "../Toast";
import { useToast } from "../../hooks/useToast";
import {
  professionalTeamService,
  ProfessionalTeamItem,
} from "../../services/professionalTeamService";
import { colors, inputs } from "../../theme/designSystem";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  value: ProfessionalTeamItem | null; // null = inclusão
  onSaved: () => void;
}

const TeamModal: React.FC<Props> = ({ isOpen, onClose, value, onSaved }) => {
  const { toast, showToast, hideToast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mainTeam, setMainTeam] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEdit = !!value;

  useEffect(() => {
    if (!isOpen) return;
    setSaving(false);
    setTitle(value?.title || "");
    setDescription(value?.description || "");
    setMainTeam(!!value?.mainTeam);
  }, [isOpen, value]);

  const handleSave = async () => {
    if (saving) return;
    if (!title.trim()) {
      showToast("Informe o nome da equipe.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        mainTeam,
      };
      if (isEdit && value) {
        await professionalTeamService.update(value.id, payload);
      } else {
        await professionalTeamService.create(payload);
      }
      onSaved();
      onClose();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao salvar a equipe.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!saving) onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", position: "relative", overflow: "hidden" },
      }}
    >
      <SavingOverlay open={saving} message="Salvando..." />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {isEdit ? "Editar equipe" : "Nova equipe"}
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Nome da equipe"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 100 }}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 255 }}
            fullWidth
            multiline
            rows={2}
            InputLabelProps={{
              shrink: inputs.multiline.labelShrink,
              sx: {
                fontSize: inputs.multiline.labelFontSize,
                color: inputs.multiline.labelColor,
                backgroundColor: inputs.multiline.labelBackground,
                padding: inputs.multiline.labelPadding,
                "&.Mui-focused": { color: colors.primary },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                position: inputs.multiline.position,
                opacity: inputs.multiline.opacity,
                alignItems: inputs.multiline.alignItems,
                fontSize: inputs.multiline.fontSize,
                minHeight: inputs.multiline.minHeight,
                maxHeight: inputs.multiline.maxHeight,
                overflow: inputs.multiline.overflow,
                padding: 0,
                "& fieldset": { borderColor: inputs.multiline.borderColor },
                "&:hover fieldset": { borderColor: inputs.multiline.borderColor },
                "&.Mui-focused fieldset": { borderColor: colors.primary },
                "& textarea": {
                  wordWrap: inputs.multiline.wordWrap,
                  whiteSpace: inputs.multiline.whiteSpace,
                  padding: inputs.multiline.inputPadding,
                  height: inputs.multiline.textareaHeight,
                  maxHeight: inputs.multiline.textareaMaxHeight,
                  overflow: `${inputs.multiline.textareaOverflow} !important`,
                  boxSizing: inputs.multiline.textareaBoxSizing,
                },
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={mainTeam}
                onChange={(e) => setMainTeam(e.target.checked)}
                disableRipple
                sx={{
                  color: colors.primary,
                  "&.Mui-checked": { color: colors.primary },
                }}
              />
            }
            label="Equipe principal da entidade"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: "1rem 1.5rem" }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: colors.primary,
            "&:hover": { backgroundColor: colors.primary, opacity: 0.9 },
          }}
        >
          Salvar
        </Button>
      </DialogActions>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Dialog>
  );
};

export default TeamModal;
